import { useState, useEffect } from "react";
import { fetchUserProfile } from "../services/userManagementApi";

// Role hierarchy: ADMIN > SPL (state) > PL (district) > PM (block) > PO (school)
const ROLE_LEVELS = { ADMIN: 5, SPL: 4, PL: 3, PM: 2, PO: 1 };

// Roles each caller is allowed to create / delete
// Nobody manages their own role level except ADMIN — mirrors
// _ALLOWED_TARGET_ROLES in services/user_management/app/api/common.py.
const ALLOWED_TARGET_ROLES = {
  ADMIN: ["ADMIN", "SPL", "PL", "PM", "PO"],
  SPL:   ["PL", "PM", "PO"],
  PL:    ["PM", "PO"],
  PM:    ["PO"],
  PO:    [],
};

/**
 * Custom hook that fetches the current user's role and provides
 * role-checking helpers for UI visibility.
 */
export function useUserRole() {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const profile = await fetchUserProfile();
        if (!cancelled) {
          setRole((profile.roles?.[0] || "PO").toUpperCase());
          setUserProfile(profile);
        }
      } catch (err) {
        console.error("useUserRole: failed to fetch profile", err);
        if (!cancelled) setRole("PO"); // safest default
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  /** True if user's role is at least `minRole` in the hierarchy */
  const hasMinRole = (minRole) =>
    (ROLE_LEVELS[role] || 0) >= (ROLE_LEVELS[minRole] || 0);

  return {
    role,
    loading,
    isAdmin: role === "ADMIN",
    /** States: only ADMIN can add/edit/delete */
    canManageStates: hasMinRole("ADMIN"),
    /** Districts: ADMIN + SPL */
    canManageDistricts: hasMinRole("SPL"),
    /** Blocks: ADMIN + SPL + PL */
    canManageBlocks: hasMinRole("PL"),
    /** Schools: ADMIN + SPL + PL + PM */
    canManageSchools: hasMinRole("PM"),
    /** Map States sidebar: ADMIN only */
    canSeeMapStates: hasMinRole("ADMIN"),
    /** Map Districts sidebar (assign districts to PL): ADMIN + SPL */
    canSeeMapDistricts: hasMinRole("SPL"),
    /** Map Blocks sidebar (assign blocks to PM): ADMIN + SPL + PL */
    canSeeMapBlocks: hasMinRole("PL"),
    /** Map Schools sidebar: ADMIN + SPL + PL + PM */
    canSeeMapSchools: hasMinRole("PM"),
    /** Users page: visible to ADMIN, SPL, PM (anyone who can manage at least one role) */
    canSeeUsers: hasMinRole("PM"),
    /** Holidays & Assessment Years: ADMIN + SPL */
    canManageHolidays: hasMinRole("SPL"),
    canManageAssessmentYears: hasMinRole("SPL"),

    // ── Cycle management helpers ──────────────────────────────────
    /** Cycles: only ADMIN + SPL can manage (create/edit/delete) */
    canManageCycles: hasMinRole("SPL"),
    /** Cycles: ADMIN + SPL + PM can view (read-only for PM) */
    canViewCycles: hasMinRole("PM"),

    // ── Competency management helpers ─────────────────────────────
    /** Competencies: only ADMIN + SPL can manage */
    canManageCompetencies: hasMinRole("SPL"),

    // ── User management helpers ──────────────────────────────────
    /** True if the user can create at least one role */
    canCreateUsers: (ALLOWED_TARGET_ROLES[role] || []).length > 0,
    /** Bulk Upload Users: same eligibility as single create-user — the
     * bulk endpoint enforces the same per-row role restriction. */
    canBulkUploadUsers: (ALLOWED_TARGET_ROLES[role] || []).length > 0,
    /** List of role values the current user is allowed to create / delete */
    allowedRoles: ALLOWED_TARGET_ROLES[role] || [],
    /** True if the current user can delete / manage a user with the given role */
    canManageUser: (targetRole) =>
      (ALLOWED_TARGET_ROLES[role] || []).includes((targetRole || "").toUpperCase()),

    /** The full user profile object (includes state_id, assignments, etc.) */
    userProfile,
  };
}
